/**
 * Helpers for picking the proxy a viewer should load.
 *
 * Pure functions with no side effects and no DOM access.
 *
 * Originals aren't always renderable in a browser: a .docx gets a PDF proxy
 * generated for it, a .psd/.tif/.exr gets an image proxy. The server also
 * withholds `original_file_url` (returns null) when proxies exist and the user
 * lacks download permission, so a viewer that only ever reads the original URL
 * breaks for review-only users.
 *
 * All matching is done on the proxy's `mime_type` rather than its
 * `file_type_id`: proxy rows don't persist their `type` column, so the Rust
 * /file/{id} endpoint reports `file_type_id` 0 for every proxy while PHP's
 * buildInitialFileData() derives it from the mime type. The mime type is the
 * only field that's right on both paths.
 *
 * `proxies` is guarded throughout because older cached payloads may omit it.
 */

import type { FileProxyInfo, GetFileResponse } from '../player-loader/loadFile';

/** The file's PDF proxy, if it has one. */
export function pdfProxy(fileData: GetFileResponse): FileProxyInfo | undefined {
  return (fileData.proxies ?? []).find((p) => p.mime_type === 'application/pdf');
}

/**
 * All of the file's image proxies, in server-preference order.
 *
 * The server has already sorted by preference (largest first, or smallest
 * first when the user's preferSD cookie is set) — the same ordering the
 * video/audio <source> list relies on — so never re-sort these.
 */
export function imageProxies(fileData: GetFileResponse): FileProxyInfo[] {
  return (fileData.proxies ?? []).filter((p) => p.mime_type.startsWith('image/'));
}

/** The file's preferred image proxy, if it has one. */
export function imageProxy(fileData: GetFileResponse): FileProxyInfo | undefined {
  return imageProxies(fileData)[0];
}

/**
 * Mime types a browser can actually decode and paint in an <img>.
 *
 * Mirrors the `$playable_mimes` list in html/include/file_utils.php (which is
 * declared but unused there), plus gif/bmp/avif — all of which browsers render
 * too. Formats like PSD, TIFF and EXR are deliberately absent: they're valid
 * image files that no browser can display, so offering the original to the
 * user would guarantee a broken view.
 */
const BROWSER_RENDERABLE_IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/svg+xml',
]);

export function isBrowserRenderableImage(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  return BROWSER_RENDERABLE_IMAGE_MIMES.has(mimeType.split(';')[0].trim().toLowerCase());
}
